import {
  type MotionStyle,
  useMotionValueEvent,
  useScroll,
} from 'framer-motion';
import { type RefObject, useRef, useState } from 'react';

export interface UseBackgroundScrollAnimateProps {
  readonly ref: RefObject<HTMLDivElement>;
  readonly shouldScrollAnimate: boolean;
}

export interface UseBackgroundScrollAnimateState {
  readonly scrollYProgress: number;
  readonly style: MotionStyle;
}

const useBackgroundScrollAnimate = ({
  ref,
  shouldScrollAnimate,
}: UseBackgroundScrollAnimateProps): UseBackgroundScrollAnimateState => {
  const initialScale = shouldScrollAnimate ? 1200 / 1440 : 1;
  const initialBorderRadius = shouldScrollAnimate ? 16 : 0;
  const [scale, setScale] = useState<number>(initialScale);
  const [borderRadius, setBorderRadius] = useState<number>(initialBorderRadius);
  const progress = useRef<number>(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start center', 'end end'],
  });

  useMotionValueEvent(scrollYProgress, 'change', (latest): void => {
    // We truncate this value and ceil it to prevent an issue where on certain devices latest can be unpredictable
    const _progress = Math.ceil(latest * 100) / 100;
    progress.current = _progress;

    if (shouldScrollAnimate) {
      const newBorderRadius = (1 - latest) * initialBorderRadius;
      const newScale = (1 - initialScale) * latest + initialScale;
      if (borderRadius !== newBorderRadius) {
        setBorderRadius(newBorderRadius);
      }
      if (scale !== newScale) {
        setScale(newScale);
      }
    }
  });

  return {
    scrollYProgress: progress.current,
    style: {
      borderRadius: shouldScrollAnimate ? borderRadius : 0,
      scale: shouldScrollAnimate ? scale : 1,
    },
  };
};

export default useBackgroundScrollAnimate;
